package ai.wagemix.data

import ai.wagemix.net.models.KxsWMArea
import ai.wagemix.net.models.KxsWMIndustry
import ai.wagemix.net.models.KxsWMOccupationTaxonomy
import ai.wagemix.net.models.WMSession
import ai.wagemix.net.models.WMSubscriptionInfo
import ai.wagemix.net.models.configureWagemix
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.firebase.WMUserData
import ai.wagemix.net.models.firebase.WMUserRole
import ai.wagemix.net.models.firebase.WMUserStatus
import ai.wagemix.net.models.firebase.of
import ai.wagemix.net.models.of
import com.fsryan.foundation.data.FSBaseVersionedTypedLocalCacheDataSource
import com.fsryan.foundation.data.FSCachedValue
import com.fsryan.foundation.data.FSLocalCacheDataSource
import com.fsryan.foundation.data.FSSystemTime
import com.fsryan.foundation.models.toEnumValue
import com.fsryan.foundation.util.log.FSLog
import com.russhwolf.settings.Settings
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlin.jvm.JvmInline
import kotlin.reflect.KClass
import kotlin.time.Instant

/*
 * The persistent cache (local-cache playbooks): small values read whole by key that must survive
 * a restart, stored through multiplatform-settings.
 *
 * Fitness: the signed-in users (the active one and any this device has seen, so the shell renders
 * before the identity provider answers), the unsaved mix draft (the design's "Unsaved mix" pill
 * survives a reload), the chart preferences, how far a logged-out visitor got through the mix
 * builder tutorial (it must not restart on every reload), and the taxonomy, areas and industries
 * lists (large-ish, slow to fetch, read whole).
 */
interface WMCache: FSLocalCacheDataSource<WMCache.Value> {

    sealed interface Value {

        /** One occupation of the unsaved mix: the weight, whether it is pinned and whether it counts. */
        @ConsistentCopyVisibility
        data class DraftOccupation internal constructor(val id: Int, val weight: Double, val locked: Boolean, val enabled: Boolean) {
            companion object
        }

        /**
         * The unsaved mix the builder edits; `sourceMixId` is the stored mix it was opened from and
         * `ownerUid` the user who was signed in when it was written (`null` when nobody was).
         */
        @ConsistentCopyVisibility
        data class MixDraft internal constructor(
            val occupations: List<DraftOccupation>,
            val areaId: Int?,
            val industryId: Int?,
            val sourceMixId: String?,
            val title: String?,
            val ownerUid: String?
        ): Value {
            companion object
        }

        /** Annual/hourly and the number of projected years. */
        @ConsistentCopyVisibility
        data class ChartPrefs internal constructor(val hourly: Boolean, val projectedYears: Int): Value {
            companion object
        }

        /**
         * How far this device got through the logged-out mix builder tutorial: the step it shows, or [Step.Finished]
         * once the visitor went through it or skipped it. Absent until the tutorial first moves on.
         */
        @ConsistentCopyVisibility
        data class MixBuilderTutorial internal constructor(val step: Step): Value {
            enum class Step { AddOccupations, ChartData, ChartMode, ChartProjection, Finished }

            companion object
        }

        @JvmInline
        value class Taxonomy internal constructor(val taxonomy: KxsWMOccupationTaxonomy): Value

        @JvmInline
        value class Areas internal constructor(val areas: List<KxsWMArea>): Value

        @JvmInline
        value class Industries internal constructor(val industries: List<KxsWMIndustry>): Value

        /**
         * One user who has signed in on this device: the identity-provider session, the `users/{uid}`
         * document and the subscription summary mirrored from it (both `null` until the first
         * mirror arrives), and whether this is the account currently signed in.
         */
        @ConsistentCopyVisibility
        data class User internal constructor(
            val session: WMSession,
            val userData: WMUserData?,
            val subscription: WMSubscriptionInfo?,
            val active: Boolean
        ): Value {
            val uid: String get() = session.uid

            companion object
        }

        /** Which cached user is signed in; reachable only through [retrieveActiveUser] and [activateUser]. */
        @JvmInline
        value class ActiveUserMarker internal constructor(val uid: String): Value
    }

    suspend fun retrieveMixDraft(): FSCachedValue<Value.MixDraft>?
    suspend fun upsertMixDraft(draft: Value.MixDraft): FSCachedValue<Value.MixDraft>
    suspend fun modifyMixDraft(modify: (Value.MixDraft?) -> Value.MixDraft?): FSCachedValue<Value.MixDraft>?
    suspend fun deleteMixDraft(): FSCachedValue<Value.MixDraft>?

    suspend fun retrieveChartPrefs(): FSCachedValue<Value.ChartPrefs>?
    suspend fun upsertChartPrefs(prefs: Value.ChartPrefs): FSCachedValue<Value.ChartPrefs>

    suspend fun retrieveMixBuilderTutorial(): FSCachedValue<Value.MixBuilderTutorial>?
    suspend fun modifyMixBuilderTutorial(
        modify: (Value.MixBuilderTutorial?) -> Value.MixBuilderTutorial?
    ): FSCachedValue<Value.MixBuilderTutorial>?

    suspend fun retrieveTaxonomy(): FSCachedValue<Value.Taxonomy>?
    suspend fun upsertTaxonomy(taxonomy: Value.Taxonomy): FSCachedValue<Value.Taxonomy>

    suspend fun retrieveAreas(): FSCachedValue<Value.Areas>?
    suspend fun upsertAreas(areas: Value.Areas): FSCachedValue<Value.Areas>

    suspend fun retrieveIndustries(): FSCachedValue<Value.Industries>?
    suspend fun upsertIndustries(industries: Value.Industries): FSCachedValue<Value.Industries>

    suspend fun retrieveUser(uid: String): FSCachedValue<Value.User>?
    suspend fun upsertUser(user: Value.User): FSCachedValue<Value.User>
    suspend fun modifyUser(uid: String, modify: (Value.User?) -> Value.User?): FSCachedValue<Value.User>?
    suspend fun deleteUser(uid: String): FSCachedValue<Value.User>?
    /** The one user with `active == true`, or `null` when nobody is signed in. */
    suspend fun retrieveActiveUser(): FSCachedValue<Value.User>?
    /** Marks [uid] active and every other cached user inactive. */
    suspend fun activateUser(uid: String): FSCachedValue<Value.User>?
    /** Marks every cached user inactive (sign-out). */
    suspend fun deactivateUsers()
}

fun WMCache.Value.DraftOccupation.Companion.create(
    id: Int,
    weight: Double,
    locked: Boolean = false,
    enabled: Boolean = true
): WMCache.Value.DraftOccupation = WMCache.Value.DraftOccupation(id, weight, locked, enabled)

fun WMCache.Value.MixDraft.Companion.create(
    occupations: List<WMCache.Value.DraftOccupation>,
    areaId: Int? = null,
    industryId: Int? = null,
    sourceMixId: String? = null,
    title: String? = null,
    ownerUid: String? = null
): WMCache.Value.MixDraft = WMCache.Value.MixDraft(occupations.toList(), areaId, industryId, sourceMixId, title, ownerUid)

/** `true` when this draft was written for [uid] (`null` = logged out); anyone else must treat it as absent. */
fun WMCache.Value.MixDraft.isOwnedBy(uid: String?): Boolean = ownerUid == uid

fun WMCache.Value.ChartPrefs.Companion.create(hourly: Boolean, projectedYears: Int): WMCache.Value.ChartPrefs {
    return WMCache.Value.ChartPrefs(hourly, projectedYears)
}

fun WMCache.Value.MixBuilderTutorial.Companion.create(
    step: WMCache.Value.MixBuilderTutorial.Step,
): WMCache.Value.MixBuilderTutorial = WMCache.Value.MixBuilderTutorial(step)

fun WMCache.Value.User.Companion.create(
    session: WMSession,
    userData: WMUserData? = null,
    subscription: WMSubscriptionInfo? = null,
    active: Boolean = true
): WMCache.Value.User = WMCache.Value.User(session, userData, subscription, active)

/*
 * Reading an update: consumers branch on the slice type, never on cache keys, which stay inside
 * the implementation.
 */

/** The slice an update is about: the value after an addition or modification, the value before a deletion. */
val FSLocalCacheDataSource.Update<WMCache.Value>.slice: WMCache.Value
    get() = when (this) {
        is FSLocalCacheDataSource.Update.Modification -> after.data
        is FSLocalCacheDataSource.Update.Addition -> after.data
        is FSLocalCacheDataSource.Update.Deletion -> before.data
    }

/** The value a slice holds after this update, or `null` when the update deleted it. */
inline fun <reified T: WMCache.Value> FSLocalCacheDataSource.Update<WMCache.Value>.valueAfter(): T? {
    return when (this) {
        is FSLocalCacheDataSource.Update.Modification -> after.data as? T
        is FSLocalCacheDataSource.Update.Addition -> after.data as? T
        is FSLocalCacheDataSource.Update.Deletion -> null
    }
}

/** `true` when this update is about a slice of type [T]. */
inline fun <reified T: WMCache.Value> FSLocalCacheDataSource.Update<WMCache.Value>.touches(): Boolean = slice is T

/** `true` when this update changes who is signed in or what is known about them. */
fun FSLocalCacheDataSource.Update<WMCache.Value>.touchesUser(): Boolean {
    return slice is WMCache.Value.User || slice is WMCache.Value.ActiveUserMarker
}

/**
 * The Settings-backed implementation. [settings] is the one `Settings` of the process (the app
 * registers it: `StorageSettings()` on the web, `MapSettings()` in tests); every key carries the
 * cache's namespace so it shares the store safely.
 */
fun WMCache(settings: Settings, systemTime: FSSystemTime = FSSystemTime): WMCache = WMCacheImpl(settings, systemTime)

internal class WMCacheImpl(
    private val settings: Settings,
    systemTime: FSSystemTime
): FSBaseVersionedTypedLocalCacheDataSource<WMCache.Value>(systemTime = systemTime), WMCache {

    private val json = Json {
        configureWagemix()
        ignoreUnknownKeys = true
    }

    private val mutex = Mutex()

    override fun acquireSettings(): Settings = settings

    override fun <T: WMCache.Value> typeTokenOf(cls: KClass<T>): String = when (cls) {
        WMCache.Value.MixDraft::class -> TOKEN_MIX_DRAFT
        WMCache.Value.ChartPrefs::class -> TOKEN_CHART_PREFS
        WMCache.Value.MixBuilderTutorial::class -> TOKEN_MIX_BUILDER_TUTORIAL
        WMCache.Value.Taxonomy::class -> TOKEN_TAXONOMY
        WMCache.Value.Areas::class -> TOKEN_AREAS
        WMCache.Value.Industries::class -> TOKEN_INDUSTRIES
        WMCache.Value.User::class -> TOKEN_USER
        WMCache.Value.ActiveUserMarker::class -> TOKEN_ACTIVE_USER
        else -> throw IllegalArgumentException("Unsupported slice: $cls")
    }

    @Suppress("UNCHECKED_CAST")
    override fun <T: WMCache.Value> typeOfToken(typeToken: String): KClass<T> = when (typeToken) {
        TOKEN_MIX_DRAFT -> WMCache.Value.MixDraft::class
        TOKEN_CHART_PREFS -> WMCache.Value.ChartPrefs::class
        TOKEN_MIX_BUILDER_TUTORIAL -> WMCache.Value.MixBuilderTutorial::class
        TOKEN_TAXONOMY -> WMCache.Value.Taxonomy::class
        TOKEN_AREAS -> WMCache.Value.Areas::class
        TOKEN_INDUSTRIES -> WMCache.Value.Industries::class
        TOKEN_USER -> WMCache.Value.User::class
        TOKEN_ACTIVE_USER -> WMCache.Value.ActiveUserMarker::class
        else -> throw IllegalArgumentException("Unsupported type token: $typeToken")
    } as KClass<T>

    override fun <T: WMCache.Value> schemaVersionOf(cls: KClass<T>): Int = when (cls) {
        WMCache.Value.MixDraft::class -> 2      // 2 added the owner uid
        else -> 1
    }

    override fun <T: WMCache.Value> serializeVersioned(key: String, value: T): String = when (value) {
        is WMCache.Value.MixDraft -> json.encodeToString(MixDraftWire.serializer(), value.toWire())
        is WMCache.Value.ChartPrefs -> json.encodeToString(ChartPrefsWire.serializer(), ChartPrefsWire(value.hourly, value.projectedYears))
        is WMCache.Value.MixBuilderTutorial -> value.step.name
        is WMCache.Value.Taxonomy -> json.encodeToString(KxsWMOccupationTaxonomy.serializer(), value.taxonomy)
        is WMCache.Value.Areas -> json.encodeToString(ListSerializer(KxsWMArea.serializer()), value.areas)
        is WMCache.Value.Industries -> json.encodeToString(ListSerializer(KxsWMIndustry.serializer()), value.industries)
        is WMCache.Value.User -> json.encodeToString(UserWire.serializer(), value.toWire())
        is WMCache.Value.ActiveUserMarker -> value.uid
        else -> throw IllegalArgumentException("Unsupported slice: $value")
    }

    @Suppress("UNCHECKED_CAST")
    override fun <T: WMCache.Value> deserializeVersioned(
        cls: KClass<T>,
        key: String,
        storedVersion: Int,
        currentVersion: Int,
        storedString: String
    ): T? = try {
        when (storedVersion) {
            // A version-1 draft has no owner; it is discarded rather than shown to whoever is signed in now.
            2 -> when (cls) {
                WMCache.Value.MixDraft::class -> json.decodeFromString(MixDraftWire.serializer(), storedString).toValue()
                else -> null
            }
            1 -> when (cls) {
                WMCache.Value.ChartPrefs::class -> json.decodeFromString(ChartPrefsWire.serializer(), storedString).toValue()
                // A step this build does not know is unreadable, so the tutorial starts over rather than guessing.
                WMCache.Value.MixBuilderTutorial::class -> WMCache.Value.MixBuilderTutorial.Step.entries
                    .firstOrNull { it.name == storedString }
                    ?.let { WMCache.Value.MixBuilderTutorial.create(it) }
                WMCache.Value.Taxonomy::class -> WMCache.Value.Taxonomy(json.decodeFromString(KxsWMOccupationTaxonomy.serializer(), storedString))
                WMCache.Value.Areas::class -> WMCache.Value.Areas(json.decodeFromString(ListSerializer(KxsWMArea.serializer()), storedString))
                WMCache.Value.Industries::class -> {
                    WMCache.Value.Industries(json.decodeFromString(ListSerializer(KxsWMIndustry.serializer()), storedString))
                }
                WMCache.Value.User::class -> json.decodeFromString(UserWire.serializer(), storedString).toValue()
                WMCache.Value.ActiveUserMarker::class -> WMCache.Value.ActiveUserMarker(storedString)
                else -> null
            }
            else -> null
        } as? T
    } catch (e: Exception) {
        FSLog.sendWMCacheDeserializeFailure(key = key, slice = cls.simpleName.orEmpty(), storedVersion = storedVersion, error = e)
        null
    }

    // --- mix draft ---

    override suspend fun retrieveMixDraft(): FSCachedValue<WMCache.Value.MixDraft>? = retrieve(KEY_MIX_DRAFT)

    override suspend fun upsertMixDraft(draft: WMCache.Value.MixDraft): FSCachedValue<WMCache.Value.MixDraft> {
        return upsertEmitting(KEY_MIX_DRAFT, draft)
    }

    override suspend fun modifyMixDraft(
        modify: (WMCache.Value.MixDraft?) -> WMCache.Value.MixDraft?
    ): FSCachedValue<WMCache.Value.MixDraft>? = modifyEmitting(KEY_MIX_DRAFT, modify)

    override suspend fun deleteMixDraft(): FSCachedValue<WMCache.Value.MixDraft>? = deleteEmitting(KEY_MIX_DRAFT)

    // --- chart prefs ---

    override suspend fun retrieveChartPrefs(): FSCachedValue<WMCache.Value.ChartPrefs>? = retrieve(KEY_CHART_PREFS)

    override suspend fun upsertChartPrefs(prefs: WMCache.Value.ChartPrefs): FSCachedValue<WMCache.Value.ChartPrefs> {
        return upsertEmitting(KEY_CHART_PREFS, prefs)
    }

    // --- mix builder tutorial ---

    override suspend fun retrieveMixBuilderTutorial(): FSCachedValue<WMCache.Value.MixBuilderTutorial>? {
        return retrieve(KEY_MIX_BUILDER_TUTORIAL)
    }

    override suspend fun modifyMixBuilderTutorial(
        modify: (WMCache.Value.MixBuilderTutorial?) -> WMCache.Value.MixBuilderTutorial?
    ): FSCachedValue<WMCache.Value.MixBuilderTutorial>? = modifyEmitting(KEY_MIX_BUILDER_TUTORIAL, modify)

    // --- reference data ---

    override suspend fun retrieveTaxonomy(): FSCachedValue<WMCache.Value.Taxonomy>? = retrieve(KEY_TAXONOMY)

    override suspend fun upsertTaxonomy(taxonomy: WMCache.Value.Taxonomy): FSCachedValue<WMCache.Value.Taxonomy> {
        return upsertEmitting(KEY_TAXONOMY, taxonomy)
    }

    override suspend fun retrieveAreas(): FSCachedValue<WMCache.Value.Areas>? = retrieve(KEY_AREAS)

    override suspend fun upsertAreas(areas: WMCache.Value.Areas): FSCachedValue<WMCache.Value.Areas> {
        return upsertEmitting(KEY_AREAS, areas)
    }

    override suspend fun retrieveIndustries(): FSCachedValue<WMCache.Value.Industries>? = retrieve(KEY_INDUSTRIES)

    override suspend fun upsertIndustries(industries: WMCache.Value.Industries): FSCachedValue<WMCache.Value.Industries> {
        return upsertEmitting(KEY_INDUSTRIES, industries)
    }

    // --- users ---

    override suspend fun retrieveUser(uid: String): FSCachedValue<WMCache.Value.User>? = retrieve(userKey(uid))

    override suspend fun upsertUser(user: WMCache.Value.User): FSCachedValue<WMCache.Value.User> {
        return upsertEmitting(userKey(user.uid), user)
    }

    override suspend fun modifyUser(
        uid: String,
        modify: (WMCache.Value.User?) -> WMCache.Value.User?
    ): FSCachedValue<WMCache.Value.User>? = modifyEmitting(userKey(uid), modify)

    override suspend fun deleteUser(uid: String): FSCachedValue<WMCache.Value.User>? = deleteEmitting(userKey(uid))

    override suspend fun retrieveActiveUser(): FSCachedValue<WMCache.Value.User>? {
        val activeUid = retrieve<WMCache.Value.ActiveUserMarker>(KEY_ACTIVE_USER)?.data?.uid ?: return null
        return retrieve<WMCache.Value.User>(userKey(activeUid))?.takeIf { it.data.active }
    }

    override suspend fun activateUser(uid: String): FSCachedValue<WMCache.Value.User>? {
        val previous = retrieve<WMCache.Value.ActiveUserMarker>(KEY_ACTIVE_USER)?.data?.uid
        if (previous != null && previous != uid) {
            modifyEmitting<WMCache.Value.User>(userKey(previous)) { current -> current?.copy(active = false) }
        }
        upsertWithoutEmitting(KEY_ACTIVE_USER, WMCache.Value.ActiveUserMarker(uid))
        return modifyEmitting(userKey(uid)) { current -> current?.copy(active = true) }
    }

    override suspend fun deactivateUsers() {
        val activeUid = retrieve<WMCache.Value.ActiveUserMarker>(KEY_ACTIVE_USER)?.data?.uid ?: return
        deleteWithoutEmitting<WMCache.Value.ActiveUserMarker>(KEY_ACTIVE_USER)
        modifyEmitting<WMCache.Value.User>(userKey(activeUid)) { current -> current?.copy(active = false) }
    }

    // --- the emission discipline of the implementation playbook ---

    private suspend fun <T: WMCache.Value> upsertEmitting(key: String, value: T): FSCachedValue<T> {
        val (before, after) = upsertWithoutEmitting(key = key, data = value)
        emitAdditionOrModification(key = key, after = after, before = before)
        return after
    }

    private suspend fun <T: WMCache.Value> deleteEmitting(key: String): FSCachedValue<T>? {
        val before = deleteWithoutEmitting<T>(key = key)
        before?.let { emitDeletion(key = key, before = it) }
        return before
    }

    private suspend fun <T: WMCache.Value> modifyEmitting(key: String, modify: (T?) -> T?): FSCachedValue<T>? {
        val (upserted, deleted) = mutex.withLock {
            val current = retrieve<T>(key = key)
            val next = modify(current?.data)
            val upserted = next?.let { upsertWithoutEmitting(key = key, data = it) }
            val deleted = if (next == null) deleteWithoutEmitting<T>(key = key) else null
            upserted to deleted
        }
        upserted?.let { (before, after) -> emitAdditionOrModification(key = key, after = after, before = before) }
        deleted?.let { emitDeletion(key = key, before = it) }
        return upserted?.second
    }

    companion object {
        private const val NAMESPACE = "ai.wagemix.data.WMCache"

        // internal, not private: the tests seed stored strings with these tokens
        internal const val TOKEN_MIX_DRAFT = "$NAMESPACE.Value.MixDraft"
        internal const val TOKEN_CHART_PREFS = "$NAMESPACE.Value.ChartPrefs"
        internal const val TOKEN_MIX_BUILDER_TUTORIAL = "$NAMESPACE.Value.MixBuilderTutorial"
        internal const val TOKEN_TAXONOMY = "$NAMESPACE.Value.Taxonomy"
        internal const val TOKEN_AREAS = "$NAMESPACE.Value.Areas"
        internal const val TOKEN_INDUSTRIES = "$NAMESPACE.Value.Industries"
        internal const val TOKEN_USER = "$NAMESPACE.Value.User"
        internal const val TOKEN_ACTIVE_USER = "$NAMESPACE.Value.ActiveUserMarker"

        private const val KEY_MIX_DRAFT = "$NAMESPACE.mixDraft"
        private const val KEY_CHART_PREFS = "$NAMESPACE.chartPrefs"
        private const val KEY_MIX_BUILDER_TUTORIAL = "$NAMESPACE.mixBuilderTutorial"
        private const val KEY_TAXONOMY = "$NAMESPACE.taxonomy"
        private const val KEY_AREAS = "$NAMESPACE.areas"
        private const val KEY_INDUSTRIES = "$NAMESPACE.industries"
        private const val KEY_ACTIVE_USER = "$NAMESPACE.activeUser"

        private fun userKey(uid: String): String = "$NAMESPACE.user:$uid"
    }
}

/* Wire shapes, one per slice; the slices themselves carry no serialization annotations. */

@Serializable
private data class DraftOccupationWire(val id: Int, val weight: Double, val locked: Boolean, val enabled: Boolean)

@Serializable
private data class MixDraftWire(
    val occupations: List<DraftOccupationWire>,
    val areaId: Int?,
    val industryId: Int?,
    val sourceMixId: String?,
    val title: String?,
    val ownerUid: String?
)

@Serializable
private data class ChartPrefsWire(val hourly: Boolean, val projectedYears: Int)

@Serializable
private data class UserDataWire(
    val deleted: Boolean,
    val email: String,
    val firstName: String?,
    val lastName: String?,
    val role: String,
    val stripeCustomerId: String?,
    val status: String,
    val subscriptionType: String,
    val updatedAtEpochMillis: Long
)

@Serializable
private data class SubscriptionWire(
    val plan: String,
    val interval: String?,
    val currentPeriodEndEpochMillis: Long?,
    val cancelAtPeriodEnd: Boolean
)

@Serializable
private data class UserWire(
    val uid: String,
    val email: String,
    val emailVerified: Boolean,
    val displayName: String?,
    val userData: UserDataWire?,
    val subscription: SubscriptionWire?,
    val active: Boolean,
    val createdAtEpochMillis: Long? = null
)

private fun WMCache.Value.MixDraft.toWire(): MixDraftWire = MixDraftWire(
    occupations = occupations.map { DraftOccupationWire(it.id, it.weight, it.locked, it.enabled) },
    areaId = areaId,
    industryId = industryId,
    sourceMixId = sourceMixId,
    title = title,
    ownerUid = ownerUid
)

private fun MixDraftWire.toValue(): WMCache.Value.MixDraft = WMCache.Value.MixDraft.create(
    occupations = occupations.map { WMCache.Value.DraftOccupation.create(it.id, it.weight, it.locked, it.enabled) },
    areaId = areaId,
    industryId = industryId,
    sourceMixId = sourceMixId,
    title = title,
    ownerUid = ownerUid
)

private fun ChartPrefsWire.toValue(): WMCache.Value.ChartPrefs = WMCache.Value.ChartPrefs.create(hourly, projectedYears)

private fun WMCache.Value.User.toWire(): UserWire = UserWire(
    uid = session.uid,
    email = session.email,
    emailVerified = session.emailVerified,
    displayName = session.displayName,
    createdAtEpochMillis = session.createdAt?.toEpochMilliseconds(),
    userData = userData?.let {
        UserDataWire(
            deleted = it.deleted,
            email = it.email,
            firstName = it.firstName,
            lastName = it.lastName,
            role = it.role.stringValue,
            stripeCustomerId = it.stripeCustomerId,
            status = it.status.stringValue,
            subscriptionType = it.subscriptionType.stringValue,
            updatedAtEpochMillis = it.updatedAt.toEpochMilliseconds()
        )
    },
    subscription = subscription?.let {
        SubscriptionWire(
            plan = it.plan.stringValue,
            interval = it.interval,
            currentPeriodEndEpochMillis = it.currentPeriodEnd?.toEpochMilliseconds(),
            cancelAtPeriodEnd = it.cancelAtPeriodEnd
        )
    },
    active = active
)

private fun UserWire.toValue(): WMCache.Value.User = WMCache.Value.User.create(
    session = WMSession.of(
        uid = uid,
        email = email,
        emailVerified = emailVerified,
        displayName = displayName,
        createdAt = createdAtEpochMillis?.let { Instant.fromEpochMilliseconds(it) },
    ),
    userData = userData?.let {
        WMUserData.of(
            deleted = it.deleted,
            email = it.email,
            firstName = it.firstName,
            lastName = it.lastName,
            role = it.role.toEnumValue(WMUserRole.User),
            stripeCustomerId = it.stripeCustomerId,
            status = it.status.toEnumValue(WMUserStatus.Created),
            subscriptionType = it.subscriptionType.toEnumValue(WMSubscriptionType.None),
            updatedAt = Instant.fromEpochMilliseconds(it.updatedAtEpochMillis)
        )
    },
    subscription = subscription?.let {
        WMSubscriptionInfo.of(
            plan = it.plan.toEnumValue(WMSubscriptionType.None),
            interval = it.interval,
            currentPeriodEnd = it.currentPeriodEndEpochMillis?.let { millis -> Instant.fromEpochMilliseconds(millis) },
            cancelAtPeriodEnd = it.cancelAtPeriodEnd
        )
    },
    active = active
)
